import http from '@/axios'

const state = {
    case: {
        nodes: [],
        organization: {},
        theme_hex_codes: [],
        read_only: false,
    },
    caseList: [],
    caseClone: {
        nodes: []
    },
    nodeOptions: [],
    chatAgents: [],
    caseNodeMap: {
        tree: {
            nodes: [],
            unattached_nodes: [],
            node_links: [],
            isLoaded: false
        },
        grid: {
            nodes: [],
            node_links: [],
            isLoaded: false,
        },
        caseID: null
    },
    mapUpdateRequired: true,
    mapViewType: 'grid',
    mapSelectedNode: {
        id: null
    },
    selectedNode: {
        id: null
    },
    selectedNodeRequiresUpdate: false,
    creativeCommonsLicenseUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
    libraryPageNumber: 0,
    librarySortField: "last_modified",
    librarySortDescending: true,
    libraryFolderId: null,
    libraryResultsPerPage: 10,
    communityLibraryCategory: undefined,
    // Set to true when an edit panel modal is currently open.
    editPanelModalIsOpen: false,
    // Set to true when a column or panel is actively being resized.
    layoutResizeIsActive: false,
}

const getters = {
    case: state => {
        return state.case
    },
    caseList: state => {
        return state.caseList
    },
    caseIsReadOnly: state => {
        return state.case.read_only
    },
    caseClone: state => {
        return state.caseClone
    },
    nodeOptions: state => {
        return state.nodeOptions
    },
    chatAgents: state => {
        return state.chatAgents
    },
    selectedNode: state => {
        return state.selectedNode
    },
    mapViewType: state => {
        return state.mapViewType
    },
    caseNodeMap: state => {
        return state.caseNodeMap[state.mapViewType]
    },
    mapSelectedNode: state => {
        return state.mapSelectedNode
    },
    caseBaseUrl: (state) => {
        return "/library/" + state.case.id
    },
    caseThemeHexColors: state => {
        return state.case.theme_hex_codes
    },
    creativeCommonsLicenseUrl: state => {
        return state.creativeCommonsLicenseUrl
    },
    editPanelModalIsOpen: state => {
        return state.editPanelModalIsOpen
    },
    librarySearchText: state => {
        return state.librarySearchText
    },
    libraryPageNumber: state => {
        return state.libraryPageNumber
    },
    libraryTagFilters: state => {
        return state.libraryTagFilters
    },
    librarySortField: state => {
        return state.librarySortField
    },
    librarySortDescending: state => {
        return state.librarySortDescending
    },
    libraryResultsPerPage: state => {
        return state.libraryResultsPerPage
    },
    libraryFolderId: state => {
        return state.libraryFolderId
    },
    communityLibraryCategory: state => {
        return state.communityLibraryCategory
    },
    layoutResizeIsActive: state => {
        return state.layoutResizeIsActive
    },
}

const mutations = {
    setCase: (state, caseObj) => {
        // Reset the map view to the grid if visiting a new case.
        if (state.case.id != caseObj.id) {
            state.mapViewType = 'grid'
        }
        state.case = caseObj
        state.caseClone = Object.assign({}, caseObj)
    },
    setCaseList: (state, caseList) => {
        state.caseList = caseList
    },
    setNodes: (state, caseObj) => {
        state.nodeOptions = caseObj.nodes
    },
    setNodeList: (state, nodes) => {
        state.nodeOptions = nodes
        state.case.nodes = nodes
        // Look for the selected node in the new node list, update the number
        // if found.
        let selectedNode = nodes.find(n => n.id == state.selectedNode.id)
        if (selectedNode) {
            state.selectedNode.number = selectedNode.number
        }
    },
    clearCase: (state) => {
        state.case = {
            nodes: []
        }
        state.caseList = []
    },
    setChatAgents: (state, agents) => {
        state.chatAgents = agents
    },
    setCaseNodeMap: (state, payload) => {
        let viewType = payload['viewType']
        let map = payload['map']
        state.caseNodeMap[viewType] = map
        state.caseNodeMap[viewType].isLoaded = true
        state.caseNodeMap.caseID = state.case.id
    },
    setSelectedNode: (state, node) => {
        state.selectedNode = node
        state.selectedNodeRequiresUpdate = false
    },
    mapRequiresUpdate: (state) => {
        state.caseNodeMap = {
            tree: {
                nodes: [],
                unattached_nodes: [],
                node_links: [],
                isLoaded: false
            },
            grid: {
                nodes: [],
                node_links: [],
                isLoaded: false,
            },
            caseID: null
        }
    },
    setMapViewType: (state, viewType) => {
        state.mapViewType = viewType
    },
    setMapSelectedNode: (state, node) => {
        state.mapSelectedNode = node
    },
    clearMapSelectedNode: state => {
        state.mapSelectedNode = { id: null }
    },
    appendFloatingNodeToMap: (state, node) => {
        state.caseNodeMap.tree.unattached_nodes.push(node)
        state.caseNodeMap.grid.nodes.push([node])
    },
    selectedNodeRequiresUpdate: (state, node) => {
        state.selectedNodeRequiresUpdate = true
    },
    setEditPanelModalIsOpen: (state, modalState) => {
        state.editPanelModalIsOpen = modalState
    },
    setLibraryPageNumber: (state, pageNum) => {
        state.libraryPageNumber = pageNum
    },
    setLibrarySortField: (state, sortField) => {
        state.librarySortField = sortField
    },
    setLibrarySortDescending: (state, sortDescending) => {
        state.librarySortDescending = sortDescending
    },
    setLibraryFolderId: (state, folderId) => {
        state.libraryFolderId = folderId
    },
    setLibraryResultsPerPage: (state, resultsPerPage) => {
        state.libraryResultsPerPage = resultsPerPage
    },
    setCommunityLibraryCategory: (state, categoryName) => {
        state.communityLibraryCategory = categoryName
    },
    setLayoutResizeIsActive: (state, active) => {
        state.layoutResizeIsActive = active
    },
    resetLibraryView: state => {
        state.libraryPageNumber = 0
        state.librarySortField = "last_modified"
        state.librarySortDescending = true
        state.libraryFolderId = null
    },
}

const actions = {
    fetchCase: (context, caseID) => {
        return context.dispatch('refreshCase', caseID)
    },
    refreshCase: (context, caseID) => {
        return new Promise((resolve, reject) => {
            http.get('/api/cases/' + caseID + '/')
                .then(function (response) {
                    context.commit('setCase', response.data)
                    context.commit('setNodes', response.data)
                    resolve(response)
                })
                .catch(function (error) {
                    reject(error)
                })
        })
    },
    clearCase: (context) => {
        context.commit('clearCase')
        context.commit('mapRequiresUpdate')
        context.dispatch('clearSelectedNode')
    },
    clearSelectedNode: (context) => {
        context.commit('setSelectedNode', {
            id: null
        })
    },
    fetchChatAgents: (context) => {
        return new Promise((resolve, reject) => {
            let org = context.state.case.organization
            http.get(
                "/api/chat-agents/", {
                params: {
                    organizationID: org ? context.state.case.organization.id : null,
                    caseID: context.state.case.id
                }
            }
            ).then(
                function (response) {
                    context.commit('setChatAgents', response.data)
                    resolve(response)
                }
            ).catch(function (error) {
                reject(error)
            })
        })
    },
    refreshCaseNodeMap: (context) => {
        return context.dispatch("fetchCaseNodeMap", {
            caseID: context.state.case.id,
            viewType: context.state.mapViewType
        })
    },
    fetchCaseNodeMap: (context, payload) => {
        let caseID = payload['caseID']
        let viewType = payload['viewType']
        return new Promise((resolve, reject) => {
            let state = context.state

            // Store the current map view type.
            context.commit('setMapViewType', viewType)

            if (
                state.caseNodeMap[viewType].isLoaded &&
                caseID == state.caseNodeMap.caseID
            ) {
                // Fetch the map structure from memory.
                resolve(state.caseNodeMap[viewType])
            } else {
                // Fetch the map structure from the server.
                http.get(
                    `/api/cases/${caseID}/node-map/`, {
                    params: {
                        viewType: viewType
                    }
                }
                ).then(
                    function (response) {
                        context.commit('setCaseNodeMap', {
                            map: response.data,
                            viewType: viewType
                        })
                        resolve(response.data)
                    }
                ).catch(function (error) {
                    reject(error)
                })
            }
        })
    },
    fetchNode: (context, nodeID) => {
        // Don't reload if we already have the node stored.
        if (context.state.selectedNode.id == nodeID && !context.state.selectedNodeRequiresUpdate) {
            return new Promise((resolve) => {
                resolve(context.state.selectedNode)
            })
        }
        return context.dispatch('refreshNode', nodeID)
    },
    refreshNode: (context, nodeID) => {
        return new Promise((resolve, reject) => {
            http.get("/api/nodes/" + nodeID + "/").then(function (response) {
                // Save a clone of the selected node in the data store.
                context.commit("setSelectedNode", Object.assign({}, response.data))
                resolve(response.data)
            }).catch(function (error) {
                reject(error)
            })
        })
    },
}

export default {
    state,
    getters,
    mutations,
    actions
}
